import { COOKIE_NAMES } from 'constants/cookies';
import useSetToCookie, { CookieName } from 'hooks/common/use-set-to-cookie';
import { useRouter } from 'next/router';
import { FC, ReactNode, createContext, useContext, useMemo } from 'react';
import { useCookieStore } from 'stores/cookie';
import {
  CampaignSegment,
  UTM_PARAM,
  isCampaignSegment,
  matchCampaignSegment,
} from 'utils/campaign-segment';
import { getStringParam } from 'utils/query-helper';

interface PropTypes {
  children: ReactNode;
}

const CampaignSegmentContext = createContext<CampaignSegment>(CampaignSegment.Other);

/** Resolves the visitor's first-touch campaign segment from the landing UTMs and shares it with the React tree. */
export const CampaignSegmentProvider: FC<PropTypes> = ({ children }) => {
  const { query } = useRouter();
  const cookies = useCookieStore((store) => store.cookies);

  const persisted = cookies[COOKIE_NAMES.campaignSegment];
  const hasFirstTouch = isCampaignSegment(persisted);

  const landingSegment = useMemo(() => {
    const source = getStringParam(query[UTM_PARAM.source]);
    const medium = getStringParam(query[UTM_PARAM.medium]);

    // A campaign-less request must not burn the first-touch slot with `other`.
    if (!source && !medium) {
      return undefined;
    }

    return matchCampaignSegment({ source, medium });
  }, [query]);

  useSetToCookie({
    cookieId: CookieName.CampaignSegment,
    cookieValue: hasFirstTouch ? undefined : landingSegment,
  });

  const value = hasFirstTouch ? persisted : (landingSegment ?? CampaignSegment.Other);

  return (
    <CampaignSegmentContext.Provider value={value}>{children}</CampaignSegmentContext.Provider>
  );
};

export const useCampaignSegment = (): CampaignSegment => useContext(CampaignSegmentContext);
